import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  ShieldAlert,
  Clock,
  AlertTriangle,
  XCircle,
  Gauge,
  CheckCircle,
  Filter,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { cn } from '@/app/components/ui/utils';
import { useAlerts, useAlertsSummary, useMarkNotificationsRead } from '@/hooks/use-platform';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';
import type { NotificationItem } from '@/lib/types';

const ALERT_TYPE_CONFIG: Record<
  string,
  { label: string; icon: React.ComponentType<{ className?: string }>; color: string; bg: string }
> = {
  cert_expiring: {
    label: 'Cert Expiring',
    icon: Clock,
    color: 'text-amber-600',
    bg: 'bg-amber-50 border-amber-200',
  },
  cert_expired: {
    label: 'Cert Expired',
    icon: XCircle,
    color: 'text-red-600',
    bg: 'bg-red-50 border-red-200',
  },
  inspection_overdue: {
    label: 'Inspection Overdue',
    icon: AlertTriangle,
    color: 'text-orange-600',
    bg: 'bg-orange-50 border-orange-200',
  },
  inspection_fail: {
    label: 'Inspection Failed',
    icon: ShieldAlert,
    color: 'text-red-600',
    bg: 'bg-red-50 border-red-200',
  },
  asset_problem_reported: {
    label: 'Gear Problem',
    icon: ShieldAlert,
    color: 'text-red-600',
    bg: 'bg-red-50 border-red-200',
  },
  wear_limit: {
    label: 'Wear Limit',
    icon: Gauge,
    color: 'text-purple-600',
    bg: 'bg-purple-50 border-purple-200',
  },
  near_miss: {
    label: 'Near-Miss',
    icon: AlertTriangle,
    color: 'text-yellow-600',
    bg: 'bg-yellow-50 border-yellow-200',
  },
};

function AlertTypeBadge({ type }: { type: string }) {
  const config = ALERT_TYPE_CONFIG[type];
  if (!config) return <span className="text-xs text-gray-500 dark:text-muted-foreground">{type}</span>;

  const Icon = config.icon;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border',
        config.bg,
        config.color
      )}
    >
      <Icon className="h-3 w-3" />
      {config.label}
    </span>
  );
}

function AlertRow({
  alert,
  onNavigate,
  onMarkRead,
}: {
  alert: NotificationItem;
  onNavigate: (link: string) => void;
  onMarkRead: (id: number) => void;
}) {
  const timeZone = useOrgTimeZone();
  return (
    <div
      className={cn(
        'flex items-start gap-3 px-4 py-3 border-b last:border-b-0 transition-colors cursor-pointer',
        alert.is_read ? 'bg-white dark:bg-card hover:bg-gray-50 dark:hover:bg-accent' : 'bg-blue-50/50 dark:bg-blue-950/50 hover:bg-blue-50 dark:hover:bg-blue-950'
      )}
      onClick={() => {
        if (!alert.is_read) onMarkRead(alert.id);
        if (alert.link) onNavigate(alert.link);
      }}
    >
      <div className="mt-0.5">
        {alert.is_read ? (
          <CheckCircle className="h-4 w-4 text-gray-300" />
        ) : (
          <Bell className="h-4 w-4 text-blue-500" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <AlertTypeBadge type={alert.notification_type} />
          <span className="text-xs text-gray-400">
            {formatDateTimeInTimeZone(alert.created_at, timeZone, {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </div>
        <p
          className={cn(
            'text-sm mt-1',
            alert.is_read ? 'text-gray-600 dark:text-muted-foreground' : 'font-medium text-gray-900 dark:text-foreground'
          )}
        >
          {alert.title}
        </p>
        <p className="text-xs text-gray-500 dark:text-muted-foreground mt-0.5">{alert.message}</p>
      </div>
    </div>
  );
}

export function AlertsPage() {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState<string | undefined>(undefined);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  const { data: summary } = useAlertsSummary();
  const { data: alertsData, isLoading } = useAlerts({
    type: activeFilter,
    is_read: showUnreadOnly ? 'false' : undefined,
    limit: 100,
  });
  const markReadMutation = useMarkNotificationsRead();

  const alerts = alertsData?.notifications ?? [];
  const total = alertsData?.total ?? 0;

  const handleMarkRead = (id: number) => {
    markReadMutation.mutate({ ids: [id] });
  };

  const handleMarkAllRead = () => {
    markReadMutation.mutate({
      all: true,
      types: [
        'cert_expiring',
        'cert_expired',
        'inspection_overdue',
        'inspection_fail',
        'asset_problem_reported',
        'wear_limit',
        'near_miss',
      ],
    });
  };

  const filterButtons = Object.entries(ALERT_TYPE_CONFIG).map(([key, config]) => {
    const count = summary?.by_type[key] ?? 0;
    return { key, ...config, count };
  });

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-foreground">Smart Alerts</h1>
          <p className="text-sm text-gray-500 dark:text-muted-foreground mt-1">
            Cert expiry, inspection overdue, wear limit, and safety notifications
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(summary?.total_unread_alerts ?? 0) > 0 && (
            <Button variant="outline" size="sm" onClick={handleMarkAllRead}>
              Mark all read
            </Button>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {filterButtons.map(({ key, label, icon: Icon, color, count }) => (
          <button
            key={key}
            type="button"
            onClick={() => setActiveFilter(activeFilter === key ? undefined : key)}
            className={cn(
              'flex flex-col items-center gap-1 rounded-lg border p-3 text-center transition-all',
              activeFilter === key
                ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                : 'border-gray-200 dark:border-border bg-white dark:bg-card hover:border-gray-300 hover:bg-gray-50 dark:hover:bg-accent'
            )}
          >
            <Icon className={cn('h-5 w-5', color)} />
            <span className="text-xs font-medium text-gray-700 dark:text-foreground">{label}</span>
            {count > 0 ? (
              <span className="text-lg font-bold text-gray-900 dark:text-foreground">{count}</span>
            ) : (
              <span className="text-lg font-bold text-gray-300">0</span>
            )}
          </button>
        ))}
      </div>

      {/* Filters bar */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-muted-foreground">
          <Filter className="h-4 w-4" />
          <span>
            {activeFilter
              ? ALERT_TYPE_CONFIG[activeFilter]?.label ?? activeFilter
              : 'All alerts'}
          </span>
          {activeFilter && (
            <button
              type="button"
              onClick={() => setActiveFilter(undefined)}
              className="ml-1 text-blue-600 hover:underline text-xs"
            >
              Clear
            </button>
          )}
        </div>
        <span className="text-gray-300">|</span>
        <label className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-muted-foreground cursor-pointer">
          <input
            type="checkbox"
            checked={showUnreadOnly}
            onChange={(e) => setShowUnreadOnly(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          Unread only
        </label>
        <span className="ml-auto text-xs text-gray-400">
          {total} alert{total !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Alert list */}
      <div className="bg-white dark:bg-card border border-gray-200 dark:border-border rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="py-12 text-center text-sm text-gray-500 dark:text-muted-foreground">Loading alerts...</div>
        ) : alerts.length > 0 ? (
          alerts.map((alert) => (
            <AlertRow
              key={alert.id}
              alert={alert}
              onNavigate={(link) => navigate(link)}
              onMarkRead={handleMarkRead}
            />
          ))
        ) : (
          <div className="py-12 text-center">
            <Bell className="mx-auto h-10 w-10 text-gray-300 mb-3" />
            <p className="text-sm text-gray-500 dark:text-muted-foreground">
              {activeFilter || showUnreadOnly
                ? 'No alerts match the current filter'
                : 'No alerts — all clear!'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
